<template>
  <modal name="note-commission" height="auto" :scrollable="true" :width="modalWidth" @before-open="onBeforeOpen">
    <div class="p-6 max-h-[85vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-4 border-b border-gray-200 pb-3">
        <div>
          <h3 class="text-lg font-semibold text-gray-800">{{ $t('note_commission.title') }}</h3>
          <p class="text-sm text-gray-500 mt-0.5">{{ agreement ? agreement.agreement_number : '' }} — {{ agreement ? agreement.customer_name : '' }}</p>
        </div>
        <button @click="close" class="text-gray-400 hover:text-gray-600 transition-colors">
          <BaseIcon icon="times" />
        </button>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-securdia-blue"></div>
        <span class="ml-3 text-sm text-gray-500">{{ $t('note_commission.loading_products') }}</span>
      </div>

      <template v-else-if="agreement">
        <!-- Agreement Info -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div class="bg-gray-50 rounded-lg p-2.5">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{{ $t('note_commission.category') }}</p>
            <p class="text-sm font-semibold text-gray-700">{{ getCategoryLabel(agreement.agreement_category) }}</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-2.5">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{{ $t('note_commission.contract_period') }}</p>
            <p class="text-sm font-semibold text-gray-700">{{ contractPeriod }} {{ $t('note_commission.months') }}</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-2.5">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{{ $t('note_commission.alarm_center') }}</p>
            <p class="text-sm font-semibold text-gray-700">{{ alarmCenterName || '-' }}</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-2.5">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{{ $t('note_commission.simcard') }}</p>
            <p class="text-sm font-semibold text-gray-700">{{ agreement.sim_card || '-' }}</p>
          </div>
        </div>

        <!-- Products Table -->
        <div class="mb-5">
          <h4 class="text-sm font-semibold text-gray-700 mb-2">{{ $t('note_commission.products') }}</h4>
          <div class="border border-gray-200 rounded-lg overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">{{ $t('note_commission.product_name') }}</th>
                  <th class="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">{{ $t('note_commission.qty') }}</th>
                  <th class="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase">{{ $t('note_commission.sale_price') }}</th>
                  <th class="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase">{{ $t('note_commission.purchase_price') }}</th>
                  <th class="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">{{ $t('note_commission.type_label') }}</th>
                  <th class="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase">TB</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="(product, idx) in enrichedProducts" :key="idx" class="hover:bg-gray-50">
                  <td class="px-3 py-2">
                    <span class="text-gray-800">{{ product.displayName }}</span>
                    <span v-if="!product.catalogMatch" class="ml-1 text-[10px] text-amber-600 font-medium">({{ $t('note_commission.manual_price') }})</span>
                  </td>
                  <td class="px-3 py-2 text-center text-gray-600">{{ product.qty }}</td>
                  <td class="px-3 py-2 text-right text-gray-600">{{ formatCurrency(product.salePrice) }}</td>
                  <td class="px-3 py-2 text-right">
                    <span v-if="product.catalogMatch" class="text-gray-600">
                      {{ formatCurrency(product.purchasePrice) }}
                      <span v-if="product.isSoftware" class="text-[10px] text-gray-400">/{{ $t('note_commission.month_short') }}</span>
                    </span>
                    <input
                      v-else
                      type="number"
                      min="0"
                      v-model.number="product.purchasePrice"
                      class="w-20 px-2 py-1 text-right border border-amber-300 rounded text-sm bg-amber-50 focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                      @input="recalculate"
                    />
                  </td>
                  <td class="px-3 py-2 text-center">
                    <span v-if="product.category === 'simcard'" class="text-blue-600 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold rounded">
                      {{ $t('note_commission.recurring') }}
                    </span>
                    <span v-else-if="product.isSoftware" class="text-purple-600 bg-purple-50 px-1.5 py-0.5 text-[10px] font-semibold rounded">
                      {{ $t('note_commission.software') }}
                    </span>
                    <span v-else :class="product.isCash ? 'text-orange-600 bg-orange-50' : 'text-blue-600 bg-blue-50'" class="px-1.5 py-0.5 text-[10px] font-semibold rounded">
                      {{ product.isCash ? $t('note_commission.cash') : $t('note_commission.rental') }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-right font-medium" :class="product.tb >= 0 ? 'text-green-600' : 'text-red-600'">
                    {{ formatCurrency(product.tb) }}
                  </td>
                </tr>
                <!-- Alarm center cost row -->
                <tr v-if="alarmCenterQuantity > 0 && alarmCenterCostPerUnit > 0" class="bg-blue-50/50">
                  <td class="px-3 py-2 text-gray-500 italic">{{ $t('note_commission.alarm_center_costs') }}</td>
                  <td class="px-3 py-2 text-center text-gray-600">{{ alarmCenterQuantity }}</td>
                  <td class="px-3 py-2 text-right text-gray-500">-</td>
                  <td class="px-3 py-2 text-right text-gray-600">{{ formatCurrency(alarmCenterCostPerUnit) }}/{{ $t('note_commission.month_short') }}</td>
                  <td class="px-3 py-2 text-center">
                    <span class="text-blue-600 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold rounded">{{ $t('note_commission.recurring') }}</span>
                  </td>
                  <td class="px-3 py-2 text-right font-medium text-red-600">-{{ formatCurrency(alarmCenterTotalCost) }}</td>
                </tr>
                <!-- SIM card cost row -->
                <tr v-if="simcardQuantity > 0 && simcardCostPerUnit > 0" class="bg-blue-50/50">
                  <td class="px-3 py-2 text-gray-500 italic">{{ $t('note_commission.simcard_costs') }}</td>
                  <td class="px-3 py-2 text-center text-gray-600">{{ simcardQuantity }}</td>
                  <td class="px-3 py-2 text-right text-gray-500">-</td>
                  <td class="px-3 py-2 text-right text-gray-600">{{ formatCurrency(simcardCostPerUnit) }}/{{ $t('note_commission.month_short') }}</td>
                  <td class="px-3 py-2 text-center">
                    <span class="text-blue-600 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold rounded">{{ $t('note_commission.recurring') }}</span>
                  </td>
                  <td class="px-3 py-2 text-right font-medium text-red-600">-{{ formatCurrency(simcardTotalCost) }}</td>
                </tr>
                <!-- Setup fee row -->
                <tr v-if="setupFeeCost > 0" class="bg-orange-50/50">
                  <td class="px-3 py-2 text-gray-500 italic">{{ $t('note_commission.setup_fee_costs') }}</td>
                  <td class="px-3 py-2 text-center text-gray-600">{{ alarmCenterQuantity }}</td>
                  <td class="px-3 py-2 text-right text-gray-500">-</td>
                  <td class="px-3 py-2 text-right text-gray-600">995 kr/{{ $t('note_commission.alarm_short') }}</td>
                  <td class="px-3 py-2 text-center">
                    <span class="text-orange-600 bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold rounded">{{ $t('note_commission.cash') }}</span>
                  </td>
                  <td class="px-3 py-2 text-right font-medium text-red-600">-{{ formatCurrency(setupFeeCost) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Redraw (OMT) costs -->
        <div v-if="isRedraw && (redrawRemainingContractCost > 0 || redrawOriginalCommissionPaid > 0)" class="mb-5 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <h4 class="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-1.5">
            <svg class="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
            {{ $t('note_commission.redraw_costs_title') }}
          </h4>
          <p class="text-xs text-amber-700 mb-3">{{ $t('note_commission.redraw_costs_description', { original: agreement.original_agreement_number }) }}</p>
          <div class="space-y-2">
            <div v-if="redrawRemainingContractCost > 0" class="flex justify-between items-center bg-white rounded px-3 py-2 border border-amber-100">
              <div>
                <span class="text-sm text-gray-700">{{ $t('note_commission.redraw_remaining_contract') }}</span>
                <p class="text-[10px] text-gray-400">{{ $t('note_commission.redraw_remaining_hint') }}</p>
              </div>
              <span class="text-sm font-semibold text-red-600">-{{ formatCurrency(redrawRemainingContractCost) }}</span>
            </div>
            <div v-if="redrawOriginalCommissionPaid > 0" class="flex justify-between items-center bg-white rounded px-3 py-2 border border-amber-100">
              <div>
                <span class="text-sm text-gray-700">{{ $t('note_commission.redraw_original_commission') }}</span>
                <p class="text-[10px] text-gray-400">{{ $t('note_commission.redraw_commission_hint') }}</p>
              </div>
              <span class="text-sm font-semibold text-red-600">-{{ formatCurrency(redrawOriginalCommissionPaid) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2 border-t border-amber-200">
              <span class="text-sm font-semibold text-amber-800">{{ $t('note_commission.redraw_total_deduction') }}</span>
              <span class="text-sm font-bold text-red-700">-{{ formatCurrency(totalRedrawCost) }}</span>
            </div>
          </div>
        </div>

        <!-- TB Summary -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div class="rounded-lg p-3 text-center" :class="totalRevenue > 0 ? 'bg-blue-50' : 'bg-gray-50'">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{{ $t('note_commission.total_revenue') }}</p>
            <p class="text-lg font-bold text-blue-700">{{ formatCurrency(totalRevenue) }}</p>
          </div>
          <div class="rounded-lg p-3 text-center bg-gray-50">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{{ $t('note_commission.hardware_cost') }}</p>
            <p class="text-lg font-bold text-gray-700">{{ formatCurrency(hardwareCost) }}</p>
          </div>
          <div class="rounded-lg p-3 text-center bg-purple-50">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{{ $t('note_commission.variable_cost') }}</p>
            <p class="text-lg font-bold text-purple-700">{{ formatCurrency(variableCost) }}</p>
          </div>
          <div class="rounded-lg p-3 text-center" :class="adjustedTB >= 0 ? 'bg-green-50' : 'bg-red-50'">
            <p class="text-[10px] uppercase tracking-wider text-gray-400 font-medium">TB</p>
            <p class="text-lg font-bold" :class="adjustedTB >= 0 ? 'text-green-700' : 'text-red-700'">{{ formatCurrency(adjustedTB) }}</p>
            <p class="text-[10px] font-medium" :class="adjustedMarginPercent >= 0 ? 'text-green-500' : 'text-red-500'">({{ adjustedMarginPercent.toFixed(1) }}%)</p>
          </div>
        </div>

        <!-- Extra costs -->
        <div class="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 class="text-sm font-semibold text-gray-700 mb-3">{{ $t('note_commission.extra_costs_title') }}</h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">{{ $t('sales_dashboard.additional_recurring_cost') }}</label>
              <div class="relative">
                <input
                  v-model.number="additionalRecurringCost"
                  type="number"
                  min="0"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-securdia-blue focus:border-securdia-blue"
                  placeholder="0"
                  @input="recalculate"
                />
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">kr/{{ $t('note_commission.month_short') }}</span>
              </div>
              <p class="text-[10px] text-gray-400 mt-0.5">{{ $t('sales_dashboard.additional_recurring_cost_hint') }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">{{ $t('sales_dashboard.additional_cash_cost') }}</label>
              <div class="relative">
                <input
                  v-model.number="additionalCashCost"
                  type="number"
                  min="0"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-securdia-blue focus:border-securdia-blue"
                  placeholder="0"
                  @input="recalculate"
                />
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">kr</span>
              </div>
              <p class="text-[10px] text-gray-400 mt-0.5">{{ $t('sales_dashboard.additional_cash_cost_hint') }}</p>
            </div>
          </div>

        </div>

        <!-- Commission Breakdown -->
        <div class="mb-5">
          <h4 class="text-sm font-semibold text-gray-700 mb-2">{{ $t('note_commission.commission_breakdown') }}</h4>
          <div class="border border-gray-200 rounded-lg overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th v-for="(_, idx) in commissionByYear" :key="idx" class="px-3 py-2 text-xs font-medium text-gray-500 uppercase text-center">
                    {{ $t('note_commission.year') }} {{ idx + 1 }}
                  </th>
                  <th class="px-3 py-2 text-xs font-medium text-gray-500 uppercase text-center bg-green-50">{{ $t('note_commission.total') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="(amount, idx) in commissionByYear" :key="idx" class="px-3 py-2 text-center font-medium text-gray-700">
                    {{ formatCurrency(amount) }}
                  </td>
                  <td class="px-3 py-2 text-center font-bold text-green-700 bg-green-50">{{ formatCurrency(commissionTotal) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Seller info -->
        <div v-if="sellerName" class="mb-5 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p class="text-xs text-blue-700">
            <span class="font-semibold">{{ $t('note_commission.seller') }}:</span> {{ sellerName }}
          </p>
        </div>

        <!-- Error message -->
        <div v-if="error" class="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            @click="close"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {{ $t('sales_dashboard.cancel') }}
          </button>
          <button
            @click="confirmCommission"
            :disabled="saving"
            class="px-5 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style="background-color: #d97706;"
          >
            <span v-if="saving" class="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
            {{ $t('note_commission.confirm_commission') }}
          </button>
        </div>
      </template>
    </div>
  </modal>
</template>

<script>
import {
  SIMCARD_CATEGORIES,
  SETUP_FEE_PER_ALARM,
  calculateCommissionByYear,
  calculateCostDeduction,
  formatCurrency as sharedFormatCurrency,
  buildCommissionEntry
} from '@/utils/commission';

export default {
  name: 'NoteCommission',
  props: {
    alarmCenters: {
      type: Array,
      default: () => []
    },
    sellers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      modalWidth: Math.min(window.innerWidth, 800),
      agreement: null,
      productCatalog: [],
      enrichedProducts: [],
      additionalRecurringCost: 0,
      additionalCashCost: 0,
      loading: false,
      saving: false,
      error: null,
      // Redraw (OMT) cost data
      originalAgreement: null,
      originalCommission: null
    };
  },
  computed: {
    contractPeriod() {
      if (!this.agreement || !this.agreement.contract_period) return 0;
      const match = this.agreement.contract_period.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    },

    contractYears() {
      return this.contractPeriod / 12;
    },

    isCashAgreement() {
      if (!this.agreement) return false;
      return this.agreement.agreement_category === 'cash' || this.agreement.agreement_category === 'cash_services';
    },

    alarmCenterName() {
      if (!this.agreement || !this.agreement.alarm_center) return '';
      const ac = this.alarmCenters.find(a => a._id === this.agreement.alarm_center);
      return ac ? ac.name : '';
    },

    alarmCenterCostPerUnit() {
      if (this.isCashAgreement) return 0;
      if (!this.alarmCenterName || this.productCatalog.length === 0) return 0;
      const needle = this.alarmCenterName.trim().toLowerCase();
      const product = this.productCatalog.find(
        p => p.category === 'alarm_central_supplier' && p.name && p.name.trim().toLowerCase() === needle
      );

      return product ? (product.purchase_price || 0) : 0;
    },

    simcardCostPerUnit() {
      if (this.isCashAgreement) return 0;
      if (!this.agreement || !this.agreement.sim_card) return 0;
      if (this.productCatalog.length === 0) return 0;
      const needle = this.agreement.sim_card.trim().toLowerCase();
      const product = this.productCatalog.find(
        p => p.category === 'simcard_supplier' && p.name && p.name.trim().toLowerCase() === needle
      );
      return product ? (product.purchase_price || 0) : 0;
    },

    // Count ALL alarm devices (regardless of cash/rental) — each device needs alarm center monitoring.
    alarmCenterQuantity() {
      return this.enrichedProducts
        .filter(p => p.category === 'alarm')
        .reduce((sum, p) => sum + p.qty, 0);
    },

    // One-time setup fee per alarm device
    setupFeeCost() {
      return this.alarmCenterQuantity * SETUP_FEE_PER_ALARM;
    },

    // Count rental devices that need simcard (alarm, display, vehicle_tracker categories).
    // Fall back to 1 if the agreement has a SIM card linked but no matching product categories.
    simcardQuantity() {
      return this.enrichedProducts
        .filter(p => SIMCARD_CATEGORIES.includes(p.category) && !p.isCash)
        .reduce((sum, p) => sum + p.qty, 0);
    },

    totalServiceCostPerMonth() {
      return (this.alarmCenterQuantity * this.alarmCenterCostPerUnit) +
             (this.simcardQuantity * this.simcardCostPerUnit);
    },

    totalServiceCost() {
      return this.totalServiceCostPerMonth;
    },

    alarmCenterTotalCost() {
      return this.alarmCenterQuantity * this.alarmCenterCostPerUnit * this.contractPeriod;
    },

    simcardTotalCost() {
      return this.simcardQuantity * this.simcardCostPerUnit * this.contractPeriod;
    },

    totalRevenue() {
      return this.enrichedProducts.reduce((sum, p) => sum + p.revenue, 0);
    },

    totalCost() {
      // Product costs + service costs + setup fees over contract period
      const productCosts = this.enrichedProducts.reduce((sum, p) => sum + p.cost, 0);
      const serviceCosts = this.totalServiceCostPerMonth * this.contractPeriod;
      return productCosts + serviceCosts + this.setupFeeCost;
    },

    hardwareCost() {
      // Non-software product costs (hardware, cash purchases) — setup fee stored separately
      return this.enrichedProducts
        .filter(p => !p.isSoftware)
        .reduce((sum, p) => sum + p.cost, 0);
    },

    softwareCost() {
      // Software product costs + alarm center & SIM card service fees
      const productCosts = this.enrichedProducts
        .filter(p => p.isSoftware)
        .reduce((sum, p) => sum + p.cost, 0);
      const serviceCosts = this.totalServiceCostPerMonth * this.contractPeriod;
      return productCosts + serviceCosts;
    },

    variableCost() {
      // Rörliga: software/service costs + extra cost deductions entered by user (reactive)
      return this.softwareCost + this.costDeduction;
    },

    isRedraw() {
      return !!(this.agreement && this.agreement.original_agreement_number);
    },

    redrawRemainingContractCost() {
      if (!this.isRedraw || !this.originalAgreement) return 0;
      const orig = this.originalAgreement;
      const periodMatch = (orig.contract_period || '').match(/(\d+)/);
      if (!periodMatch || !orig.contract_start) return 0;
      const totalMonths = parseInt(periodMatch[1]);
      const contractEnd = this.moment(orig.contract_start).add(totalMonths, 'months');
      const now = this.moment();
      const remainingMonths = Math.min(totalMonths, Math.max(0, Math.ceil(contractEnd.diff(now, 'months', true))));
      const monthlyCost = parseFloat(orig.rental_month_cost) || 0;
      return remainingMonths * monthlyCost;
    },

    redrawOriginalCommissionPaid() {
      if (!this.isRedraw || !this.originalCommission) return 0;
      return this.originalCommission.total_commission || 0;
    },

    totalRedrawCost() {
      return this.redrawRemainingContractCost + this.redrawOriginalCommissionPaid;
    },

    adjustedMarginPercent() {
      return this.totalRevenue > 0 ? (this.adjustedTB / this.totalRevenue) * 100 : 0;
    },

    totalTB() {
      return this.totalRevenue - this.totalCost;
    },

    marginPercent() {
      return this.totalRevenue > 0 ? (this.totalTB / this.totalRevenue) * 100 : 0;
    },

    costDeduction() {
      return calculateCostDeduction(this.additionalRecurringCost, this.additionalCashCost, this.contractPeriod);
    },

    adjustedTB() {
      return this.totalTB - this.costDeduction - this.totalRedrawCost;
    },

    _commissionResult() {
      return calculateCommissionByYear(this.adjustedTB, this.contractPeriod, this.sellerCommissionPercentage);
    },

    commissionByYear() {
      return this._commissionResult.byYear;
    },

    commissionTotal() {
      return this._commissionResult.total;
    },

    sellerCommissionPercentage() {
      if (!this.agreement || !this.agreement.seller || !this.sellers) return 7;
      const seller = this.sellers.find(s => s._id === this.agreement.seller);
      return seller && seller.commission_percentage != null ? seller.commission_percentage : 7;
    },

    sellerName() {
      if (!this.agreement || !this.agreement.seller || !this.sellers) return '';
      const seller = this.sellers.find(s => s._id === this.agreement.seller);
      return seller ? seller.name : '';
    },

    sellerId() {
      if (!this.agreement || !this.agreement.seller || !this.sellers) return null;
      const seller = this.sellers.find(s => s._id === this.agreement.seller);
      return seller ? seller._id : null;
    }
  },
  methods: {
    async onBeforeOpen(event) {
      this.error = null;
      this.additionalRecurringCost = 0;
      this.additionalCashCost = 0;
      this.enrichedProducts = [];
      this.productCatalog = [];
      this.originalAgreement = null;
      this.originalCommission = null;

      if (event.params && event.params.agreement) {
        this.agreement = event.params.agreement;

        // Check if we need the product catalog:
        // 1. Alarm center / simcard costs always need catalog lookup
        // 2. Products without embedded purchase_price need catalog lookup
        const needsCatalogForServices = !!(this.agreement.alarm_center || this.agreement.sim_card);
        const products = this.agreement.products || [];
        // Load catalog whenever any product has a missing or zero purchase_price so the
        // catalog-first lookup below can override a stale embedded 0.
        const needsCatalogForProducts = products.some(p => !p.purchase_price);

        const promises = [];
        if (needsCatalogForServices || needsCatalogForProducts) {
          promises.push(this.loadProductCatalog());
        }
        // Fetch redraw cost data if this is a redrawn agreement
        if (this.agreement.original_agreement_number) {
          promises.push(this.loadRedrawCosts());
        }
        if (promises.length > 0) await Promise.all(promises);

        this.buildEnrichedProducts();
      }
    },

    async loadProductCatalog() {
      this.loading = true;
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`);
        this.productCatalog = response.data.data || [];
      } catch (err) {
        console.error('Failed to load product catalog:', err);
        this.error = this.$t('note_commission.load_error');
      } finally {
        this.loading = false;
      }
    },

    async loadRedrawCosts() {
      try {
        const params = {};
        if (this.sellerId) params.seller_id = this.sellerId;
        const resp = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/agreements/redraw-costs/${encodeURIComponent(this.agreement.original_agreement_number)}`,
          { params }
        );
        this.originalAgreement = resp.data.original || null;
        this.originalCommission = resp.data.commission || null;
      } catch (err) {
        console.error('Failed to load redraw costs:', err);
      }
    },

    buildEnrichedProducts() {
      if (!this.agreement || !Array.isArray(this.agreement.products)) {
        this.enrichedProducts = [];
        return;
      }

      // Helper to check if a product has any quantity (parent or variant level)
      const hasAnyQty = (p) => {
        if (!p || typeof p !== 'object') return false;
        if ((p.qty || 0) > 0 || (p.quantity || 0) > 0) return true;
        // Check variant quantities
        if (p.variants && Array.isArray(p.variants)) {
          return p.variants.some(v => (v.qty || 0) > 0 || (v.quantity || 0) > 0);
        }
        return false;
      };

      const results = [];

      this.agreement.products
        .filter(hasAnyQty)
        .forEach(agreementProduct => {
          // Match against product catalog
          let catalogMatch = this.findProductInCatalog(agreementProduct);
          let category = agreementProduct.category || '';

          if (catalogMatch) {
            category = category || catalogMatch.category || '';
          }

          // Determine if this is a cash purchase or rental
          const isCashCategory = this.agreement.agreement_category === 'cash' || this.agreement.agreement_category === 'cash_services';
          const isMonthlyOrSoftware = ['addon_monthly', 'other_monthly', 'software', 'simcard'].includes(agreementProduct.type) ||
                                       (catalogMatch && catalogMatch.is_software);
          const isCash = agreementProduct.type === 'cash' || agreementProduct.type === 'one_time' ||
                         agreementProduct.type === 'addon_cash' || agreementProduct.type === 'other_cash' ||
                         (isCashCategory && !isMonthlyOrSoftware);
          // Software/recurring products have a recurring purchase cost (licence fee per month)
          // Products with category 'simcard' (e.g. "Extra simcard", "Extra alarm central") are recurring monthly costs
          const isSoftware = agreementProduct.type === 'software' ||
                             (catalogMatch && catalogMatch.is_software === true) ||
                             Boolean(agreementProduct.is_software) ||
                             category === 'simcard';

          // Check if variants have qty > 0 (variant-based product)
          const activeVariants = (agreementProduct.variants || []).filter(v => (v.qty || 0) > 0 || (v.quantity || 0) > 0);
          const parentQty = agreementProduct.qty || agreementProduct.quantity || 0;

          if (activeVariants.length > 0) {
            // Process each active variant as a separate line item
            activeVariants.forEach(variant => {
              const variantQty = variant.qty || variant.quantity || 0;
              let purchasePrice = 0;
              let displayName = agreementProduct.display_name || agreementProduct.name || 'Okänd produkt';

              // Catalog takes priority over embedded price (embedded may be a stale 0 from creation time)
              const embeddedVariantPrice = variant.purchase_price;
              const embeddedProductPrice = agreementProduct.purchase_price;

              if (catalogMatch) {
                const catalogVariant = (catalogMatch.variants || []).find(cv =>
                  cv.name === variant.name ||
                  (variant.articleNumber && cv.articleNumber === variant.articleNumber) ||
                  (variant._id && String(cv._id) === String(variant._id))
                );

                if (catalogVariant) {
                  purchasePrice = catalogVariant.purchase_price ?? catalogMatch.purchase_price ?? 0;
                  displayName = `${catalogMatch.display_name || catalogMatch.name} - ${variant.name}`;
                } else {
                  purchasePrice = catalogMatch.purchase_price || 0;
                  displayName = `${catalogMatch.display_name || catalogMatch.name} - ${variant.name}`;
                }
              } else if (embeddedVariantPrice != null) {
                // No catalog match — use embedded variant price as fallback
                purchasePrice = embeddedVariantPrice;
                displayName = `${displayName} - ${variant.name}`;
              } else if (embeddedProductPrice != null) {
                // No catalog match — use embedded parent price as fallback
                purchasePrice = embeddedProductPrice;
                displayName = `${displayName} - ${variant.name}`;
              } else {
                displayName = `${displayName} - ${variant.name}`;
              }

              // Get variant sale price
              let salePrice = 0;
              if (isCash) {
                salePrice = variant.cash_price || variant.price || 0;
              } else {
                salePrice = variant.rental_price || variant.price || 0;
              }

              // Calculate revenue and cost
              let revenue, cost, tb;
              if (isCash) {
                revenue = variantQty * salePrice;
                cost = variantQty * purchasePrice;
                tb = revenue - cost;
              } else {
                revenue = variantQty * salePrice * this.contractPeriod;
                // Software purchase price is recurring — multiply by contract period
                cost = isSoftware
                  ? variantQty * purchasePrice * this.contractPeriod
                  : variantQty * purchasePrice;
                tb = revenue - cost;
              }

              results.push({
                product_id: agreementProduct.product_id || (catalogMatch && catalogMatch._id) || null,
                variant_id: variant._id || null,
                displayName,
                qty: variantQty,
                salePrice,
                purchasePrice,
                isCash,
                isSoftware,
                category,
                type: agreementProduct.type || '',
                catalogMatch: !!catalogMatch || embeddedVariantPrice != null || embeddedProductPrice != null,
                revenue,
                cost,
                tb
              });
            });
          }
          
          // Also process parent product if it has qty > 0 AND no active variants
          // (when variants are active, they already capture the full quantity — skip parent to avoid duplicates)
          if (parentQty > 0 && activeVariants.length === 0) {
            let purchasePrice = 0;
            let displayName = agreementProduct.display_name || agreementProduct.name || 'Okänd produkt';
            const isFlatVariant = !!(agreementProduct.variant_id || agreementProduct.variant_name);

            // Catalog takes priority over embedded price (embedded may be a stale 0 from creation time)
            const embeddedPrice = agreementProduct.purchase_price;

            if (catalogMatch) {
              let variantMatch = null;
              if (activeVariants.length === 0 && catalogMatch.variants && catalogMatch.variants.length > 0) {
                variantMatch = this.findVariantMatch(catalogMatch, agreementProduct);
              }

              if (variantMatch) {
                purchasePrice = variantMatch.purchase_price ?? catalogMatch.purchase_price ?? 0;
                displayName = `${catalogMatch.display_name || catalogMatch.name} - ${variantMatch.name}`;
              } else {
                purchasePrice = catalogMatch.purchase_price || 0;
                // Don't override displayName for flat variant products
                if (!isFlatVariant) displayName = catalogMatch.display_name || catalogMatch.name || displayName;
              }
            } else if (embeddedPrice != null) {
              // No catalog match — use embedded price as fallback
              purchasePrice = embeddedPrice;
            }

            // Get the sale price from agreement
            let salePrice = 0;
            if (isCash) {
              salePrice = agreementProduct.cash_price || agreementProduct.price || 0;
            } else {
              salePrice = agreementProduct.rental_price || agreementProduct.price || 0;
            }

            // Calculate revenue and cost
            let revenue, cost, tb;
            if (isCash) {
              revenue = parentQty * salePrice;
              cost = parentQty * purchasePrice;
              tb = revenue - cost;
            } else {
              revenue = parentQty * salePrice * this.contractPeriod;
              // Software purchase price is recurring — multiply by contract period
              cost = isSoftware
                ? parentQty * purchasePrice * this.contractPeriod
                : parentQty * purchasePrice;
              tb = revenue - cost;
            }

            results.push({
              product_id: agreementProduct.product_id || (catalogMatch && catalogMatch._id) || null,
              variant_id: agreementProduct.variant_id || null,
              displayName,
              qty: parentQty,
              salePrice,
              purchasePrice,
              isCash,
              isSoftware,
              category,
              type: agreementProduct.type || '',
              catalogMatch: !!catalogMatch || embeddedPrice != null,
              revenue,
              cost,
              tb
            });
          }
        });

      this.enrichedProducts = results;
    },

    findProductInCatalog(agreementProduct) {
      if (!this.productCatalog || this.productCatalog.length === 0) return null;

      // Try matching by product_id first
      if (agreementProduct.product_id) {
        const match = this.productCatalog.find(p => p._id === agreementProduct.product_id);
        if (match) return match;
      }

      // Try matching by type
      if (agreementProduct.type) {
        const match = this.productCatalog.find(p => p.type === agreementProduct.type);
        if (match) return match;
      }

      // Try matching by name
      if (agreementProduct.name) {
        const match = this.productCatalog.find(p =>
          p.name === agreementProduct.name ||
          p.name.toLowerCase() === agreementProduct.name.toLowerCase()
        );
        if (match) return match;
      }

      return null;
    },

    findVariantMatch(catalogProduct, agreementProduct) {
      if (!catalogProduct.variants || catalogProduct.variants.length === 0) return null;

      // Check nested variants
      if (agreementProduct.variants && agreementProduct.variants.length > 0) {
        const av = agreementProduct.variants[0];
        return catalogProduct.variants.find(v =>
          v.name === av.name ||
          (av.articleNumber && v.articleNumber === av.articleNumber)
        ) || null;
      }

      // Check flat variant format
      if (agreementProduct.variant_id || agreementProduct.variant_name) {
        return catalogProduct.variants.find(v =>
          String(v._id) === String(agreementProduct.variant_id) ||
          v.name === agreementProduct.variant_name ||
          (agreementProduct.articleNumber && v.articleNumber === agreementProduct.articleNumber)
        ) || null;
      }

      return null;
    },

    recalculate() {
      // Trigger reactivity for manual price changes
      this.enrichedProducts = [...this.enrichedProducts.map(p => {
        let revenue, cost, tb;
        if (p.isCash) {
          revenue = p.qty * p.salePrice;
          cost = p.qty * p.purchasePrice;
          tb = revenue - cost;
        } else {
          revenue = p.qty * p.salePrice * this.contractPeriod;
          // Software purchase price is recurring — must also multiply by contract period
          cost = p.isSoftware
            ? p.qty * p.purchasePrice * this.contractPeriod
            : p.qty * p.purchasePrice;
          tb = revenue - cost;
        }
        return { ...p, revenue, cost, tb };
      })];
    },

    getCategoryLabel(category) {
      const categoryMap = {
        'rental': 'Hyra',
        'cash': 'Kontant',
        'services_software': 'Tjänst/Mjukvara',
        'cash_services': 'Kontant + Tjänst',
        'financed': 'Finansierad'
      };
      return categoryMap[category] || category || '-';
    },

    formatCurrency(value) {
      return sharedFormatCurrency(value);
    },

    async confirmCommission() {
      if (!this.agreement || this.saving) return;

      this.saving = true;
      this.error = null;

      try {
        // Build product list for the commission entry
        const commissionProducts = this.enrichedProducts.map(p => ({
          product_id: p.product_id || undefined,
          variant_id: p.variant_id || undefined,
          name: p.displayName,
          type: p.type || undefined,
          category: p.category || undefined,
          qty: p.qty,
          sale_price: p.salePrice,
          purchase_price: p.purchasePrice,
          is_cash: p.isCash,
          is_software: p.isSoftware,
        }));

        // SIM card info — only include if there are devices that need SIM cards
        let simcardEntry = undefined;
        if (this.agreement.sim_card && this.simcardCostPerUnit > 0 && this.simcardQuantity > 0) {
          const simProduct = this.productCatalog.find(
            p => p.category === 'simcard_supplier' && p.name && p.name.trim().toLowerCase() === this.agreement.sim_card.trim().toLowerCase()
          );
          simcardEntry = {
            product_id: simProduct ? simProduct._id : undefined,
            name: this.agreement.sim_card,
            cost_per_month: this.simcardCostPerUnit,
            qty: this.simcardQuantity,
          };
        }

        // Alarm center info — only include if there are alarm devices
        let alarmCenterEntry = undefined;
        if (this.alarmCenterName && this.alarmCenterCostPerUnit > 0 && this.alarmCenterQuantity > 0) {
          const acProduct = this.productCatalog.find(
            p => p.category === 'alarm_central_supplier' && p.name && p.name.trim().toLowerCase() === this.alarmCenterName.trim().toLowerCase()
          );
          alarmCenterEntry = {
            product_id: acProduct ? acProduct._id : undefined,
            name: this.alarmCenterName,
            cost_per_month: this.alarmCenterCostPerUnit,
            qty: this.alarmCenterQuantity,
          };
        }

        // Build commission entry for the seller
        const commissionEntry = buildCommissionEntry({
          agreementNumber: this.agreement.agreement_number,
          customerName: this.agreement.customer_name,
          contractPeriod: this.contractPeriod,
          commissionByYear: this.commissionByYear,
          commissionTotal: this.commissionTotal,
          extraMonthlyCost: this.additionalRecurringCost,
          extraCashCost: this.additionalCashCost,
          totalTB: this.totalTB,
          adjustedTB: this.adjustedTB,
          totalRevenue: this.totalRevenue,
          totalCost: this.totalCost,
          hardwareCost: this.hardwareCost,
          softwareCost: this.softwareCost,
          setupFeeCost: this.setupFeeCost,
          marginPercent: this.adjustedMarginPercent,
          pdfFile: this.agreement.pdf_file || null,
          products: commissionProducts,
          simcard: simcardEntry,
          alarmCenter: alarmCenterEntry,
          originalAgreementNumber: this.isRedraw ? this.agreement.original_agreement_number : undefined,
          redrawRemainingContractCost: this.isRedraw ? this.redrawRemainingContractCost : undefined,
          redrawOriginalCommissionPaid: this.isRedraw ? this.redrawOriginalCommissionPaid : undefined,
          redrawTotalCost: this.isRedraw ? this.totalRedrawCost : undefined
        });

        // 1. Mark agreement as commission_noted
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/agreements/${this.agreement._id}`,
          { commission_noted: true }
        );

        // 2. Save commission to the seller's commissions array
        if (this.sellerId) {
          try {
            const staffResponse = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/staff/${this.sellerId}`);
            const seller = staffResponse.data.data || staffResponse.data;
            const existingCommissions = seller.commissions || [];

            // Prevent duplicate: skip if a commission for this agreement already exists
            const alreadyExists = existingCommissions.some(
              c => c.agreement_number === this.agreement.agreement_number
            );
            if (!alreadyExists) {
              existingCommissions.push(commissionEntry);

              await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/staff/${this.sellerId}`, {
                commissions: existingCommissions
              });
            }
          } catch (staffError) {
            console.error('Error saving commission to seller:', staffError);
            // Commission is still noted on agreement — don't block
          }
        }

        // Emit event to refresh agreements list
        this.$emit('commission-noted');
        this.close();
      } catch (err) {
        console.error('Error noting commission:', err);
        this.error = this.$t('sales_dashboard.commission_note_error');
      } finally {
        this.saving = false;
      }
    },

    close() {
      this.$modal.hide('note-commission');
    }
  }
};
</script>
